import * as bitcoin from 'bitcoinjs-lib';
import { ECPairFactory } from 'ecpair';
import { bech32 } from 'bech32';
import * as ecc from '@bitcoinerlab/secp256k1';

// Use the browser-tested ECC library directly
// @bitcoinerlab/secp256k1 is tiny-secp256k1 compatible and works in browsers

// Lazy initialization to avoid validation errors during import
let ECPair = null;
const getECPair = () => {
  if (!ECPair) {
    ECPair = ECPairFactory(ecc);
  }
  return ECPair;
};

class WalletService {
  // Network parameters - default HRP set to Mytherra (myt)
  get addressPrefix() {
    return this._addressPrefix || 'myt';
  }
  
  set addressPrefix(value) {
    this._addressPrefix = value;
  }
  
  get networkPrefix() {
    return this._networkPrefix !== undefined ? this._networkPrefix : 0x80;
  }
  
  set networkPrefix(value) {
    this._networkPrefix = value;
  }

  /**
   * Generate a new wallet (CLIENT-SIDE)
   * Returns: { privateKey: WIF, address: Bech32 }
   */
  async generateNewWallet() {
    try {
      // Generate random key pair
      const keyPair = getECPair().makeRandom();
      
      // Get WIF private key
      const privateKey = keyPair.toWIF();
      
      // Get public key
      const publicKey = keyPair.publicKey;
      
      // Create P2WPKH (witness pubkey hash)
      const pubKeyHash = bitcoin.crypto.hash160(publicKey);
      
      // Convert to bech32 address with witness version 0
      const words = bech32.toWords(pubKeyHash);
      const address = bech32.encode(this.addressPrefix, [0, ...words]);
      
      return {
        privateKey: privateKey,
        address: address
      };
    } catch (error) {
      console.error('Wallet generation error:', error);
      throw error;
    }
  }

  /**
   * Get Bech32 address from WIF private key (CLIENT-SIDE)
   */
  async getAddressFromWif(wifPrivateKey) {
    try {
      // Import private key from WIF
      const keyPair = getECPair().fromWIF(wifPrivateKey);
      
      // Get public key
      const publicKey = keyPair.publicKey;
      
      // Create P2WPKH (witness pubkey hash)
      const pubKeyHash = bitcoin.crypto.hash160(publicKey);
      
      // Convert to bech32 address with witness version 0
      const words = bech32.toWords(pubKeyHash);
      const address = bech32.encode(this.addressPrefix, [0, ...words]);
      
      return address;
    } catch (error) {
      console.error('Error getting address from WIF:', error);
      return null;
    }
  }

  /**
   * Get UTXOs for an address via RPC
   */
  async getUtxos(rpcUrl, rpcUser, rpcPassword, address) {
    try {
      // Use scantxoutset to find UTXOs
      const result = await this.rpcRequest(rpcUrl, rpcUser, rpcPassword, 'scantxoutset', [
        'start',
        [`addr(${address})`]
      ]);

      if (!result || !result.result) return [];

      const utxos = result.result.unspents || [];

      // Get blockchain height for confirmations
      const blockchainInfo = await this.rpcRequest(rpcUrl, rpcUser, rpcPassword, 'getblockchaininfo');
      const currentHeight = blockchainInfo?.result?.blocks || 0;

      // Map UTXOs with confirmations
      return utxos.map(utxo => {
        let confirmations = 0;
        if (utxo.height && utxo.height > 0) {
          confirmations = currentHeight - utxo.height + 1;
        }
        
        return {
          txid: utxo.txid,
          vout: utxo.vout,
          amount: parseFloat(utxo.amount) || 0.0,
          confirmations: confirmations,
        };
      }).filter(utxo => utxo.confirmations > 0); // Only confirmed UTXOs
    } catch (e) {
      console.error('Error getting UTXOs:', e);
      return [];
    }
  }

  /**
   * Calculate balance from UTXOs
   */
  calculateBalance(utxos) {
    return utxos.reduce((sum, utxo) => sum + (utxo.amount || 0), 0.0);
  }

  /**
   * Get balance from Explorer API (fallback)
   */
  async getBalanceFromExplorer(address) {
    try {
      console.log('[EXPLORER] Fetching balance for', address);
      // Use proxy to avoid CORS issues
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const API_BASE = isLocalhost ? 'http://localhost:3002' : '/api';
      const response = await fetch(`${API_BASE}/explorer/ext/getbalance?address=${address}`);
      
      console.log('[EXPLORER] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[EXPLORER] Response data:', data);
        
        if (data.balance !== undefined) {
          return parseFloat(data.balance);
        }
      }
    } catch (e) {
      console.error('[EXPLORER] Error:', e);
    }
    return null;
  }

  /**
   * Send transaction
   */
  async sendTransaction(rpcUrl, rpcUser, rpcPassword, privateKeyWif, fromAddress, toAddress, amount) {
    try {
      // Get UTXOs
      const utxos = await this.getUtxos(rpcUrl, rpcUser, rpcPassword, fromAddress);

      if (utxos.length === 0) {
        return { success: false, message: 'No confirmed UTXOs available' };
      }

      // Sort by amount (largest first)
      utxos.sort((a, b) => b.amount - a.amount);

      // Select UTXOs
      const selectedUtxos = [];
      let inputSum = 0.0;
      const feeRate = 0.00001; // MYT per byte

      for (const utxo of utxos) {
        selectedUtxos.push({ txid: utxo.txid, vout: utxo.vout });
        inputSum += utxo.amount;

        // Calculate fee based on TX size
        const inputCount = selectedUtxos.length;
        const outputCount = 2; // recipient + change
        const txSize = 10 + (inputCount * 148) + (outputCount * 34);
        const fee = (feeRate * txSize) / 1000;

        if (inputSum >= amount + fee) {
          const actualFee = parseFloat(fee.toFixed(8));
          const change = parseFloat((inputSum - amount - actualFee).toFixed(8));

          // Prepare outputs
          const outputs = {
            [toAddress]: parseFloat(amount.toFixed(8)),
          };

          // Add change output if above dust limit
          if (change > 0.00000546) {
            outputs[fromAddress] = change;
          }

          // Create raw transaction
          const createResult = await this.rpcRequest(
            rpcUrl, rpcUser, rpcPassword,
            'createrawtransaction',
            [selectedUtxos, outputs]
          );

          if (!createResult || !createResult.result) {
            return { success: false, message: 'Failed to create transaction' };
          }

          // Sign transaction
          const signResult = await this.rpcRequest(
            rpcUrl, rpcUser, rpcPassword,
            'signrawtransactionwithkey',
            [createResult.result, [privateKeyWif]]
          );

          if (!signResult || !signResult.result) {
            return { success: false, message: 'Failed to sign transaction' };
          }

          if (!signResult.result.complete) {
            return { success: false, message: 'Transaction signature incomplete' };
          }

          // Send transaction
          const sendResult = await this.rpcRequest(
            rpcUrl, rpcUser, rpcPassword,
            'sendrawtransaction',
            [signResult.result.hex, 0]
          );

          if (sendResult && sendResult.result) {
            return {
              success: true,
              txid: sendResult.result,
              fee: actualFee,
            };
          }

          const errorMessage = sendResult?.error?.message || 'Unknown error';
          return { success: false, message: errorMessage };
        }
      }

      return {
        success: false,
        message: `Insufficient funds. Available: ${inputSum.toFixed(8)} MYT`
      };
    } catch (e) {
      console.error('Error sending transaction:', e);
      return { success: false, message: e.message };
    }
  }

  /**
   * RPC request helper
   */
  async rpcRequest(rpcUrl, rpcUser, rpcPassword, method, params = []) {
    const auth = 'Basic ' + btoa(`${rpcUser}:${rpcPassword}`);
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': auth,
    };

    const body = JSON.stringify({
      jsonrpc: '1.0',
      id: 'web',
      method: method,
      params: params,
    });

    try {
      const response = await fetch(rpcUrl, {
        method: 'POST',
        headers: headers,
        body: body,
      });

      console.log('[RPC]', method, 'Status:', response.status);
      const data = await response.json();
      console.log('[RPC]', method, 'Response:', data);
      
      return data;
    } catch (e) {
      console.error('[RPC] ERROR:', method, e);
      return null;
    }
  }

  /**
   * Apply network parameters
   */
  applyNetworkParams({ hrp, wifPrefix }) {
    if (hrp && hrp.length > 0) this.addressPrefix = hrp;
    if (wifPrefix !== undefined) this.networkPrefix = wifPrefix;
  }
}

// Export singleton instance
export default new WalletService();
